import { useState } from 'react';
import { Loader2, MessageSquare, Sparkles } from 'lucide-react';
import { explainAgentAction } from '../api';
import type { AgentActionDetail } from '../types';

interface ActionDetailExplainerProps {
  detail: AgentActionDetail;
}

export default function ActionDetailExplainer({ detail }: ActionDetailExplainerProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleExplain = async () => {
    setOpen(true);
    if (text && !error) return;
    setLoading(true);
    setError(null);
    try {
      const explanation = await explainAgentAction(detail);
      setText(explanation);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not generate explanation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={handleExplain}
        disabled={loading}
        className="inline-flex items-center gap-1.5 text-sm border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/40 text-violet-800 dark:text-violet-200 rounded-md px-3 py-1.5 hover:bg-violet-100 dark:hover:bg-violet-900/40 disabled:opacity-60"
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
        Ask Claude what was changed
      </button>

      {open && (
        <div className="mt-4 rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50/60 dark:bg-violet-950/25 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-violet-600 dark:text-violet-300" />
            <p className="text-sm font-medium text-violet-900 dark:text-violet-100 m-0">Claude summary</p>
          </div>
          {loading && (
            <p className="text-sm text-gray-600 dark:text-gray-400 m-0">Analyzing this action…</p>
          )}
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400 m-0">{error}</p>
          )}
          {text && !loading && (
            <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
              {text}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
